// Agent Builder chat panel — shared between Agent Library ("Create with AI"
// + per-agent edit-via-chat) AND the Builder canvas (AI-assist Cmd+K). It
// connects to the tenant's Super Agent (a.k.a. "Agent Builder") via
// WebSocket chat and lets the user describe, create, and edit agents
// conversationally.
//
// History: extracted from AgentLibraryView.jsx so the Builder canvas's
// AI assist surface uses the EXACT same component (no behavioral drift
// between the two entry points).
//
// Behavioral fixes vs. the original inline copy:
//   * `panelMode` defaults to "floating" (start undocked). Users found
//     the docked default cramped beside the canvas; floating gives full
//     control over the position.
//   * Top of the panel cleared the app's TopBar via TOPBAR_HEIGHT so
//     the panel header is no longer hidden behind the nav.

import { useState, useEffect, useRef, useCallback } from "react";
import { COLORS } from "../../theme/colors";
import ArtifactChips from "../skills/ArtifactChips";
import AgentServingLight from "../admin/AgentServingLight";
import { agentApi, filesApi } from "../../api/client";
import { useWebSocketChat } from "../../hooks/useWebSocketChat";
import { MarkdownText } from "../../components/MarkdownText";
import { ChatCitations } from "../../components/ChatCitations";
import { VerificationBadge } from "../../components/VerificationBadge";
import { SupervisorMessageDecorations } from "../../components/SupervisorMessageDecorations";
import TreeProposalCard, { extractTreeProposal } from "./TreeProposalCard";
import TestScriptOffer, { extractTestScriptOffer } from "./TestScriptOffer";
import Icon from "../../components/Icon";

// Height of the app's top nav bar — the docked panel anchors below it
// instead of `top: 0` (was hiding the chat header behind the nav).
const TOPBAR_HEIGHT = 56;

function RichTableElement({ el }) {
  const headers = el.headers || [];
  const rows = el.rows || [];
  return (
    <div style={{ marginTop: 8, overflowX: "auto", border: `1px solid ${COLORS.border}`, borderRadius: 6 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} style={{ padding: "6px 10px", borderBottom: `1px solid ${COLORS.border}`, textAlign: "left", color: COLORS.textMuted, fontWeight: 600 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <td key={ci} style={{ padding: "5px 10px", borderTop: `1px solid ${COLORS.border}`, color: COLORS.text }}>{String(cell)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Internal "thinking" tool names whose chip is HIDDEN from the
// strip — they're helpers the Super Agent uses to gather context
// (list existing agents, suggest an integration archetype) and the
// user-visible signal of their effect already shows up elsewhere
// (e.g., suggest_integration's output is the integration_placeholder
// in the tree_proposal card right above the chat strip — duplicating
// it as a chip is just noise). Tools that DO change tenant state
// (provision_agent_tree, register_openapi_integration, create_agent,
// etc.) stay visible — those are actionable and worth surfacing.
const INTERNAL_THINKING_TOOLS = new Set([
  "suggest_integration",
  "list_existing_agents", "list_agents",
  "list_available_models", "list_models",
  "list_mcp_servers", "list_openapi_integrations", "list_workflows",
  "get_agent_config", "get_agent_performance", "get_all_agents_performance",
  "obtain_openapi_spec",
  "pick_standard_experts",
]);

function visibleToolCalls(toolCalls) {
  if (!Array.isArray(toolCalls)) return [];
  // 1) Drop hidden internal-thinking tools.
  const visible = toolCalls.filter(tc => {
    const name = tc?.name || tc?.display_name || "";
    return !INTERNAL_THINKING_TOOLS.has(name);
  });
  // 2) Dedupe runs of the SAME tool name (very common: the LLM
  //    sometimes calls provision_agent_tree 10× back-to-back; instead of
  //    rendering 10 chips, render one chip with a count badge `(N×)`
  //    aggregating their results. Latest call wins for success/error —
  //    matches retry semantics (a recovered call shouldn't show fail).
  const out = [];
  for (const tc of visible) {
    const name = tc?.name || tc?.display_name || "";
    const last = out[out.length - 1];
    if (last && (last.name || last.display_name) === name) {
      last._count = (last._count || 1) + 1;
      last.success = tc.success;
      last.args = tc.args || last.args;
      last.result = tc.result || last.result;
      last.error_type = tc.success ? null : (tc.error_type || last.error_type);
      last.duration_ms = (last.duration_ms || 0) + (tc.duration_ms || 0);
    } else {
      out.push({ ...tc, _count: 1 });
    }
  }
  return out;
}

// Tier-2 #2: tool-call result rendering. Renders one chip per tool call
// the orchestrator surfaced under msg.toolCalls. Each chip shows
// `display_name ✓ 455ms` (success) or `display_name ✗ channel_not_found`
// (logical error). Click a chip → expands to show:
//   * The extracted error message at the top (for upstream_logical_error
//     entries; matches the dispatcher's "ERROR: <body.error>\n..." format).
//   * Pretty-printed args JSON (cred fields already scrubbed server-side).
//   * Pretty-printed result JSON (capped at 2KB by the orchestrator).
function ToolCallStrip({ toolCalls }) {
  const [expanded, setExpanded] = useState(null);

  const formatJsonish = (raw) => {
    if (raw === null || raw === undefined) return "";
    if (typeof raw !== "string") raw = String(raw);
    // Try to reformat as pretty JSON; if not parseable, return as-is.
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  };

  return (
    <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {toolCalls.map((tc, i) => {
          const isOk = !!tc.success;
          const errType = tc.error_type;
          const errLabel =
            errType === "upstream_logical_error" ? "logical error"
            : errType === "auth_failed" ? "auth failed"
            : errType === "rate_limited" ? "rate limited"
            : errType === "upstream_error" ? "5xx"
            : errType === "client_error" ? "4xx"
            : errType === "transport_error" ? "transport error"
            : errType === "circuit_open" ? "circuit open"
            : errType ? errType
            : null;
          const isOpen = expanded === i;
          return (
            <button
              key={i}
              onClick={() => setExpanded(isOpen ? null : i)}
              title={isOk ? `${tc.display_name} succeeded` : `${tc.display_name} failed: ${errLabel || "unknown"}`}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "3px 9px", borderRadius: 999,
                border: `1px solid ${isOk ? (COLORS.green || "#10b981") : (COLORS.pink || "#ef4444")}40`,
                background: `${isOk ? (COLORS.green || "#10b981") : (COLORS.pink || "#ef4444")}15`,
                color: COLORS.text, fontSize: 11, fontFamily: "monospace",
                cursor: "pointer",
                opacity: tc.lease_action === "replay" ? 0.55 : 1,
              }}
            >
              <span aria-hidden="true">{"\uD83D\uDD27"}</span>
              <span>{tc.display_name || tc.name || "tool"}</span>
              {tc._count > 1 && (
                <span style={{
                  fontSize: 9, fontWeight: 700,
                  padding: "1px 5px", borderRadius: 4,
                  background: `${COLORS.accent}20`, color: COLORS.accentText,
                }}>×{tc._count}</span>
              )}
              <span style={{ color: COLORS.textMuted }}>
                {isOk ? "\u2713" : "\u2717"} {tc.latency_ms || tc.duration_ms || 0}ms
                {tc.lease_action === "replay" ? " · cached" : ""}
              </span>
              {!isOk && errLabel && (
                <span style={{ color: COLORS.pink || "#ef4444" }}>· {errLabel}</span>
              )}
            </button>
          );
        })}
      </div>

      {expanded !== null && toolCalls[expanded] && (() => {
        const tc = toolCalls[expanded];
        const isOk = !!tc.success;
        return (
          <div style={{
            marginTop: 6, padding: "8px 10px", borderRadius: 8,
            border: `1px solid ${COLORS.border}`,
            background: COLORS.surface,
            fontSize: 11, fontFamily: "monospace", color: COLORS.text,
            maxHeight: 360, overflow: "auto",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ color: COLORS.textMuted }}>
                {tc.server ? `${tc.server} · ` : ""}{tc.display_name}
              </span>
              <button
                onClick={() => setExpanded(null)}
                style={{ background: "transparent", border: "none", color: COLORS.textMuted, cursor: "pointer", fontSize: 14 }}
                title="Collapse"
              >{"\u2715"}</button>
            </div>
            {!isOk && tc.error_message && (
              <div style={{
                marginBottom: 8, padding: "6px 8px", borderRadius: 6,
                border: `1px solid ${(COLORS.pink || "#ef4444")}40`,
                background: `${(COLORS.pink || "#ef4444")}10`,
                color: COLORS.pink || "#ef4444",
              }}>
                ERROR: {tc.error_message}
              </div>
            )}
            <div style={{ color: COLORS.textMuted, marginBottom: 2 }}>args:</div>
            <pre style={{ margin: 0, marginBottom: 8, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {formatJsonish(tc.args) || "(none)"}
            </pre>
            <div style={{ color: COLORS.textMuted, marginBottom: 2 }}>result:</div>
            <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {formatJsonish(tc.result) || "(empty)"}
            </pre>
          </div>
        );
      })()}
    </div>
  );
}


function HoverCopyRow({ isUser, text, children }) {
  const [hovered, setHovered] = useState(false);
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text || "").then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  const copyBtn = (
    <button
      onClick={handleCopy}
      title="Copy message"
      style={{
        visibility: hovered ? "visible" : "hidden",
        background: "transparent", border: "none",
        color: COLORS.textDim, cursor: "pointer",
        padding: "2px 4px", borderRadius: 4,
        fontSize: 13, flexShrink: 0, alignSelf: "flex-end",
      }}
      onMouseEnter={e => { e.currentTarget.style.color = COLORS.text; }}
      onMouseLeave={e => { e.currentTarget.style.color = COLORS.textDim; }}
    >{copied ? "✓" : "⧉"}</button>
  );
  return (
    <div
      style={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start", alignItems: "flex-end", gap: 6 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {isUser && copyBtn}
      {children}
      {!isUser && copyBtn}
    </div>
  );
}

// Modes:
//   default          → talks to the tenant's "Agent Builder" Super Agent
//                      with a system-context message describing which
//                      agent the user is creating/editing. Used by the
//                      Library "Create with AI" button + the Builder
//                      canvas Cmd+K AI assist.
//   directMode=true  → talks DIRECTLY to the supplied `agent` (the user
//                      tests / chats with the agent itself). No
//                      system-context send; header reflects the agent
//                      name. Used by the Library tile context-menu
//                      "Test in chat" / "Chat" button.
export default function AgentBuilderChat({ agent, agents, onClose, initialMode, directMode = false }) {
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const [inputText, setInputText] = useState("");
  const [contextSent, setContextSent] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  // §19.12 — track which message indices the user has Discard-ed so the
  // TreeProposalCard renders dimmed + non-interactive without removing
  // the conversation history.
  const [dismissedProposals, setDismissedProposals] = useState(() => new Set());
  // §19.12 / G.4 — when chat is in edit mode (the agent prop carries an
  // id), fetch the current tree once so the TreeProposalCard can
  // diff-overlay the proposal: added experts green, removed red,
  // modified amber. Empty for create mode (nothing to diff against).
  const [existingTree, setExistingTree] = useState(null);
  useEffect(() => {
    if (!agent?.id) { setExistingTree(null); return; }
    let cancelled = false;
    agentApi.getTree(agent.id).then((res) => {
      if (cancelled) return;
      if (res?.success && res.data) setExistingTree(res.data);
    }).catch(() => { /* edit mode with no tree → diff just degrades to no-diff */ });
    return () => { cancelled = true; };
  }, [agent?.id]);
  const pendingFilesRef = useRef(pendingFiles);
  pendingFilesRef.current = pendingFiles;
  const [imagePreview, setImagePreview] = useState(null);

  // Default to "floating" so the panel starts undocked.
  // initialMode prop allows callers to override (e.g. mobile docked).
  const [panelMode, setPanelMode] = useState(initialMode || "floating");
  // Floating default position keeps the panel right-aligned with a top
  // offset that clears the TopBar (was hiding behind the nav at y=40).
  const [panelPos, setPanelPos] = useState({
    x: window.innerWidth - 460,
    y: TOPBAR_HEIGHT + 16,
  });
  const [panelSize, setPanelSize] = useState({
    w: 440,
    h: window.innerHeight - TOPBAR_HEIGHT - 32,
  });

  const handleDragStart = (e) => {
    if (panelMode === "docked") return;
    const startX = e.clientX - panelPos.x;
    const startY = e.clientY - panelPos.y;
    const onMove = (ev) => setPanelPos({
      x: Math.max(0, ev.clientX - startX),
      // Clamp y so the panel header can't be dragged behind the TopBar.
      y: Math.max(TOPBAR_HEIGHT, ev.clientY - startY),
    });
    const onUp = () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const handleResizeStart = (e) => {
    e.stopPropagation();
    const startX = e.clientX, startY = e.clientY;
    const startW = panelSize.w, startH = panelSize.h;
    const onMove = (ev) => setPanelSize({
      w: Math.max(320, startW + (ev.clientX - startX)),
      h: Math.max(400, startH + (ev.clientY - startY)),
    });
    const onUp = () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  // Per-conversation storage key so chat history persists across
  // panel close + reopen until the user hits "Reset" (clearSession).
  // Distinct keys per (mode, target) so a "create new agent" session
  // doesn't bleed into "edit existing" or into a direct chat with
  // some other agent. UUIDs are tenant-scoped at the row level so
  // including just agent.id is safe.
  const storageKey = directMode
    ? (agent?.id ? `chat:direct:${agent.id}` : null)
    : (agent?.id ? `chat:builder:edit:${agent.id}` : "chat:builder:new");
  const { messages, connected, streaming, agentTyping, sendMessage, sendMessageWithFiles, clearSession } = useWebSocketChat({ storageKey });
  const fileUploading = pendingFiles.some(f => f.status === "uploading" || f.status === "processing");
  // Default mode targets the tenant's "Agent Builder" Super Agent.
  // directMode targets the supplied agent directly (the user is talking
  // TO the agent, not ABOUT it).
  const builderAgent = directMode ? null : agents?.find(a => a.name === "Agent Builder" && a.status === "live");
  const targetAgentId = directMode ? (agent?.id || null) : (builderAgent?.id || null);
  const isCreateMode = !directMode && !agent.id;

  useEffect(() => {
    // directMode skips the system-context send. The user is chatting
    // directly with the agent, not asking the Super Agent to set up
    // / modify it; injecting a system-context message would just
    // confuse the agent's own pipeline (the system prompt is on the
    // agent server-side already).
    if (directMode) return;
    if (connected && targetAgentId && !contextSent && messages.length === 0) {
      const ctx = isCreateMode
        ? "[System context: The user wants to create a new agent. Ask them what kind of agent they need and guide them through the process. They may upload a flowchart image — analyze it to determine the agent's pipeline, channels, tools, guardrails, and system prompt.]"
        : `[System context: You are assisting with agent: "${agent.name}" (ID: ${agent.id}). ` +
          `Provider: ${agent.provider}, Model: ${agent.model}, Status: ${agent.status}, ` +
          `Channels: ${(agent.channels || []).join(", ") || "none"}. ` +
          `Please load this agent's config and ask me what I'd like to change. They may upload images — analyze them for pipeline changes.]`;
      sendMessage(ctx, targetAgentId);
      setContextSent(true);
    }
  }, [connected, targetAgentId, contextSent, messages.length, agent, isCreateMode, sendMessage, directMode]);

  // Pinned-to-bottom auto-scroll (preserves user scroll-up during streaming).
  // See AgentChatView for rationale — every token tick was a state update,
  // and unconditional scrollTop = scrollHeight made it impossible to scroll
  // back up while the LLM was still streaming.
  const pinnedToBottom = useRef(true);
  const handleScrollableScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    pinnedToBottom.current = distanceFromBottom < 40;
  }, []);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScrollableScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScrollableScroll);
  }, [handleScrollableScroll]);
  useEffect(() => {
    if (pinnedToBottom.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, agentTyping]);

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    for (const file of files) {
      const tempId = "upload_" + Math.random().toString(36).slice(2, 10);
      const isImage = file.type.startsWith("image/");
      const previewUrl = isImage ? URL.createObjectURL(file) : null;
      setPendingFiles(prev => [...prev, {
        id: tempId, file_id: null, filename: file.name, size: file.size,
        content_type: file.type, status: "uploading", progress: 0, isImage, previewUrl,
      }]);
      (async () => {
        setPendingFiles(prev => prev.map(f => f.id === tempId ? { ...f, status: "processing", progress: 50 } : f));
        try {
          // ARC-013: goes through filesApi.upload → client `request`, which
          // owns the 401→tryRefreshToken dance (previously re-implemented here).
          const data = await filesApi.upload(file, targetAgentId);
          if (data.success) {
            setPendingFiles(prev => prev.map(f => f.id === tempId ? { ...f, file_id: data.data.file_id, status: "complete", progress: 100 } : f));
          } else {
            setPendingFiles(prev => prev.map(f => f.id === tempId ? { ...f, status: "error", error: data.detail || "Upload failed" } : f));
          }
        } catch (err) {
          setPendingFiles(prev => prev.map(f => f.id === tempId ? { ...f, status: "error", error: err.body?.detail || err.message || "Network error" } : f));
        }
      })();
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removePendingFile = (id) => {
    setPendingFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file?.previewUrl) URL.revokeObjectURL(file.previewUrl);
      return prev.filter(f => f.id !== id);
    });
  };

  // "noTarget" replaces the original "noBuilder" — in directMode the
  // target is the agent prop itself; if missing/invalid the chat is
  // unusable for the same reason a missing Super Agent disables it.
  const noTarget = directMode ? !targetAgentId : !builderAgent;
  const noBuilder = noTarget;  // backward-compat alias used in render below
  const canSend = connected && !noTarget && !streaming && !fileUploading && (inputText.trim() || pendingFiles.some(f => f.status === "complete"));

  const handleSend = () => {
    const text = inputText.trim();
    const currentFiles = pendingFilesRef.current;
    const stillUploading = currentFiles.some(f => f.status === "uploading" || f.status === "processing");
    const completedFiles = currentFiles.filter(f => f.status === "complete" && f.file_id);
    if (stillUploading) return;
    if ((!text && !completedFiles.length) || !connected || noBuilder) return;
    const hasImages = completedFiles.some(f => f.isImage);
    const autoText = hasImages
      ? (text || "I've uploaded a flowchart/diagram. Please analyze it and create an agent based on the workflow shown in the image.")
      : (text || "Please review the attached files.");
    if (completedFiles.length > 0) {
      sendMessageWithFiles(autoText, completedFiles.map(f => f.file_id), targetAgentId);
    } else {
      sendMessage(text, targetAgentId);
    }
    currentFiles.forEach(f => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); });
    setInputText("");
    setPendingFiles([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  // Docked anchors below the TopBar (was `top: 0` → header hidden behind nav).
  const panelStyle = panelMode === "docked"
    ? { position: "fixed", top: TOPBAR_HEIGHT, right: 0, bottom: 0, width: window.innerWidth < 640 ? "100%" : 420, maxWidth: "100vw" }
    : { position: "fixed", left: panelPos.x, top: panelPos.y, width: panelSize.w, height: panelSize.h, borderRadius: 12 };

  return (
    <div
      data-testid="ai-assist-panel"
      style={{
      ...panelStyle,
      zIndex: 1000, display: "flex", flexDirection: "column",
      background: COLORS.surface,
      border: panelMode === "docked" ? "none" : `1px solid ${COLORS.border}`,
      borderLeft: panelMode === "docked" ? `1px solid ${COLORS.border}` : "none",
      boxShadow: panelMode === "docked" ? "-8px 0 32px rgba(0,0,0,0.4)" : "0 8px 48px rgba(0,0,0,0.5)",
      animation: "slideInRight 0.2s ease-out",
      overflow: "hidden",
    }}>
      <div
        onMouseDown={handleDragStart}
        style={{
          padding: "10px 14px", borderBottom: `1px solid ${COLORS.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          cursor: panelMode === "docked" ? "default" : "grab",
          userSelect: "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 28, height: 28, borderRadius: 8, flexShrink: 0,
            background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.purple})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 14, color: "#fff",
          }}>{"\uD83E\uDD16"}</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>
              {directMode ? (agent?.name || "Agent") : "Agent Builder"}
            </div>
            <div style={{ fontSize: 10, color: COLORS.textMuted }}>
              {directMode
                ? <>Direct chat {" \u00B7 "}{agent?.status || "draft"}</>
                : (isCreateMode ? "Creating new agent" : <>Editing: <strong style={{ color: COLORS.accentText }}>{agent.name}</strong></>)}
              {" \u00B7 "}{connected ? "Connected" : "Connecting..."}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* On-demand model warm-up light — renders only when this agent's
              chat is bound to a scale-to-zero model (else hides itself). */}
          {directMode && agent?.id && <AgentServingLight agentId={agent.id} />}
          <button onClick={() => {
            clearSession();
            setContextSent(false);
            setPendingFiles([]);
            setInputText("");
            setImagePreview(null);
            setDismissedProposals(new Set());
            // Note: tool-call expand state lives inside ToolCallStrip
            // (per-message scope) — clearSession wipes the messages
            // array, which unmounts those instances and resets their
            // local expand state for free. No setter to call here.
          }} title="Reset conversation" style={{
            width: 26, height: 26, borderRadius: 6, border: `1px solid ${COLORS.border}`,
            background: "transparent", color: COLORS.textMuted, cursor: "pointer", fontSize: 11,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>{"\u21BA"}</button>
          {panelMode === "docked" ? (
            <button onClick={() => { setPanelMode("floating"); setPanelPos({ x: window.innerWidth - 460, y: TOPBAR_HEIGHT + 16 }); }} title="Float window" style={{
              width: 26, height: 26, borderRadius: 6, border: `1px solid ${COLORS.border}`,
              background: "transparent", color: COLORS.textMuted, cursor: "pointer", fontSize: 11,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>{"\u2197"}</button>
          ) : (
            <button onClick={() => setPanelMode("docked")} title="Dock to side" style={{
              width: 26, height: 26, borderRadius: 6, border: `1px solid ${COLORS.border}`,
              background: "transparent", color: COLORS.textMuted, cursor: "pointer", fontSize: 11,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>{"\u2B1C"}</button>
          )}
          <button onClick={onClose} title="Close" style={{
            width: 26, height: 26, borderRadius: 6, border: `1px solid ${COLORS.border}`,
            background: "transparent", color: COLORS.textMuted, cursor: "pointer", fontSize: 13,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>{"\u00D7"}</button>
        </div>
      </div>

      {panelMode === "floating" && (
        <div
          onMouseDown={handleResizeStart}
          style={{
            position: "absolute", bottom: 0, right: 0, width: 16, height: 16,
            cursor: "nwse-resize", zIndex: 10,
            background: `linear-gradient(135deg, transparent 50%, ${COLORS.border} 50%)`,
            borderRadius: "0 0 12px 0",
          }}
        />
      )}

      {noBuilder && (
        <div style={{
          padding: "16px 18px", background: `${COLORS.orange}10`,
          borderBottom: `1px solid ${COLORS.orange}25`, fontSize: 12, color: COLORS.orange,
        }}>
          {directMode
            ? <>Agent isn't reachable. Verify it's <strong>Live</strong> and that channels include <strong>chat</strong>.</>
            : <>Agent Builder (Super Agent) not found. Go to <strong>Admin &gt; Super Agent</strong> to provision it first.</>}
        </div>
      )}

      <div ref={scrollRef} className="panel-scroll" style={{
        flex: 1, overflowY: "auto", padding: "16px 16px 8px",
        display: "flex", flexDirection: "column", gap: 10,
      }}>
        {/* Compute the visible-message count using the SAME filter as
            the rendered list. Welcome text gates on this (not on raw
            messages.length) — otherwise the system-context priming
            message dismisses the welcome the moment it lands in state,
            so the user saw a black void from priming-send → assistant-
            reply-streamed (~1-3 seconds) instead of useful onboarding
            copy. */}
        {(() => {
          const visible = messages.filter(m => !(m.role === "user" && m.content?.startsWith("[System context:")));
          return (
            <>
              {visible.length === 0 && !agentTyping && !noBuilder && (
                <div style={{ textAlign: "center", padding: "32px 20px", color: COLORS.textMuted, fontSize: 13, display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: 12,
                    background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.purple})`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 22, color: "#fff", flexShrink: 0,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
                  }}>{"\uD83E\uDD16"}</div>
                  {directMode ? (
                    <>
                      <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>
                        Chat with {agent.name}
                      </div>
                      <div style={{ fontSize: 12, color: COLORS.textDim, lineHeight: 1.55, maxWidth: 320 }}>
                        Type a message to test how this agent responds. The conversation persists until you hit Reset (the ⟲ button above).
                      </div>
                    </>
                  ) : isCreateMode ? (
                    <>
                      <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>
                        Hi! I'm Agent Builder.
                      </div>
                      <div style={{ fontSize: 12, color: COLORS.textDim, lineHeight: 1.55, maxWidth: 320 }}>
                        Tell me what kind of agent you want to create. Be as specific as you'd like — I'll handle the system prompt, channels, tools, and guardrails.
                      </div>
                      <div style={{ fontSize: 11, color: COLORS.textDim, lineHeight: 1.55, maxWidth: 320, padding: "8px 12px", background: `${COLORS.accent}10`, border: `1px solid ${COLORS.accent}25`, borderRadius: 8 }}>
                        <strong style={{ color: COLORS.accentText }}>Examples:</strong> "A customer support agent for a SaaS product" · "An agent that books meetings via Slack" · "An agent that classifies invoices"
                      </div>
                      <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>
                        📎 You can also drop in a flowchart image and I'll build the agent from it.
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>
                        Editing <strong style={{ color: COLORS.accentText }}>{agent.name}</strong>
                      </div>
                      <div style={{ fontSize: 12, color: COLORS.textDim, lineHeight: 1.55, maxWidth: 320 }}>
                        Tell me what to change — system prompt, channels, tools, knowledge base, guardrails. I'll apply edits to the live config.
                      </div>
                    </>
                  )}
                </div>
              )}
              {visible.map((msg, i) => (
          <HoverCopyRow key={i} isUser={msg.role === "user"} text={msg.content}>
            <div style={{
              maxWidth: "82%", padding: "10px 14px",
              borderRadius: msg.role === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
              background: msg.error ? `${COLORS.pink}15`
                : msg.role === "user" ? COLORS.accent
                : msg.role === "system" ? `${COLORS.orange}15`
                : COLORS.surface,
              color: msg.error ? COLORS.pink
                : msg.role === "user" ? "#fff"
                : msg.role === "system" ? COLORS.orange
                : COLORS.text,
              border: msg.role === "user" ? "none" : `1px solid ${COLORS.border}`,
              fontSize: 13, lineHeight: 1.5,
              opacity: msg.streaming ? 0.9 : 1,
            }}>
              {msg.fileIds && msg.fileIds.length > 0 && (
                <div style={{ fontSize: 11, opacity: 0.8, marginBottom: 4 }}>{"\uD83D\uDCCE"} {msg.fileIds.length} file{msg.fileIds.length > 1 ? "s" : ""} attached</div>
              )}
              {/* Decorations only fire on the final text (depend on
                  full message structure); rendering them mid-stream
                  causes flicker as token cadence reshapes the DOM. */}
              {msg.role === "assistant" && !msg.streaming && (
                <SupervisorMessageDecorations text={msg.content} />
              )}
              {/* Markdown renders progressively as tokens stream —
                  MarkdownText re-tokenizes via useMemo on each render,
                  so each appended token rebuilds the formatted DOM
                  (Claude-Code-style "transform as it appears"). User
                  messages stay as plain pre-wrap.
                  §19.12 — for assistant messages, extract the
                  ```tree_proposal block (when present) and render it
                  as a TreeProposalCard after the prose. The card stays
                  dim once the user clicks Discard. */}
              {msg.role === "assistant" ? (() => {
                // §19.12 — extract tree_proposal block (Accept-card)
                const tp = extractTreeProposal(msg.content || "");
                // §19.14 — and test_script_offer block (Download card).
                // Both can co-exist; the offer block is emitted in
                // post-creation messages while the proposal block is
                // pre-creation. Run the offer extractor on whatever's
                // left after the proposal block was stripped.
                const tso = extractTestScriptOffer(tp.contentWithoutBlock);
                return (
                  <>
                    <MarkdownText text={tso.contentWithoutBlock} />
                    {tp.proposal && !msg.streaming && (
                      <TreeProposalCard
                        proposal={tp.proposal}
                        existingTree={existingTree}
                        dismissed={dismissedProposals.has(i)}
                        onAccept={(_proposal, captured) => {
                          // BUG-022 — pin which proposal the user is
                          // accepting by appending the proposal JSON as
                          // an `accepted_proposal` fence block. Without
                          // this, the Super Agent LLM has to guess
                          // which `tree_proposal` in scrollback to
                          // pass to `provision_agent_tree`, which fails
                          // when the user iterated on multiple
                          // proposals in the same conversation.
                          const acceptedBlock = "```accepted_proposal\n" +
                            JSON.stringify(_proposal, null, 2) +
                            "\n```";
                          // §19.15 / G.2 — when the user registered
                          // integrations via the Configure modal, append
                          // an `integration_registrations` fence so the
                          // LLM threads the captured ids into
                          // provision_agent_tree's bindings. Both
                          // fences co-exist in the same acceptance
                          // message — they pin different aspects of
                          // the create call (proposal identity +
                          // integration ids).
                          if (captured && captured.length > 0) {
                            const intBlock = "```integration_registrations\n" +
                              JSON.stringify(captured, null, 2) +
                              "\n```";
                            sendMessage(
                              "Yes, please go ahead and create it now.\n\n"
                                + acceptedBlock + "\n\n" + intBlock,
                              targetAgentId,
                            );
                          } else {
                            sendMessage(
                              "Yes, please go ahead and create it now.\n\n"
                                + acceptedBlock,
                              targetAgentId,
                            );
                          }
                        }}
                        onRefine={(text) => sendMessage(text, targetAgentId)}
                        onDiscard={() => setDismissedProposals(prev => {
                          const next = new Set(prev);
                          next.add(i);
                          return next;
                        })}
                      />
                    )}
                    {tso.offer && !msg.streaming && (
                      <TestScriptOffer
                        agentId={tso.offer.agent_id}
                        agentName={tso.offer.agent_name}
                      />
                    )}
                  </>
                );
              })() : (
                <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{msg.content}</span>
              )}
              {msg.streaming && <span style={{ opacity: 0.5 }}>{"\u258C"}</span>}
              {msg.role === "assistant" && msg.richElements && msg.richElements.map((el, j) => el.type === "table" ? <RichTableElement key={j} el={el} /> : el.type === "artifacts" ? <ArtifactChips key={j} artifacts={el.artifacts} /> : null)}
              {(() => {
                if (msg.role !== "assistant") return null;
                const visible = visibleToolCalls(msg.toolCalls);
                if (visible.length === 0) return null;
                return <ToolCallStrip toolCalls={visible} />;
              })()}
              {msg.role === "assistant" && msg.citations && msg.citations.length > 0 && (
                <ChatCitations citations={msg.citations} agentId={agent?.id} />
              )}
              {msg.role === "assistant" && !msg.streaming && (
                <VerificationBadge verification={msg.verification} />
              )}
            </div>
          </HoverCopyRow>
              ))}
              {agentTyping && (
                <div style={{ display: "flex", gap: 4, padding: "8px 0" }}>
                  {[0, 1, 2].map(i => (
                    <div key={i} style={{
                      width: 6, height: 6, borderRadius: "50%", background: COLORS.textDim,
                      animation: `fadeIn 0.6s ${i * 0.2}s infinite alternate`,
                    }} />
                  ))}
                </div>
              )}
            </>
          );
        })()}
      </div>

      {pendingFiles.length > 0 && (
        <div style={{ padding: "8px 16px 0", display: "flex", flexWrap: "wrap", gap: 6 }}>
          {pendingFiles.map(f => (
            <div key={f.id} style={{
              display: "flex", alignItems: "center", gap: 6, padding: "4px 10px",
              borderRadius: 8, fontSize: 11, background: COLORS.surface, border: `1px solid ${COLORS.border}`,
              color: f.status === "error" ? COLORS.pink : COLORS.text,
            }}>
              {f.isImage && f.previewUrl && (
                <img src={f.previewUrl} alt="" style={{
                  width: 28, height: 28, borderRadius: 4, objectFit: "cover", cursor: "pointer",
                }} onClick={() => setImagePreview({ url: f.previewUrl, filename: f.filename })} />
              )}
              {f.status === "uploading" && <span style={{ color: COLORS.accentText }}>{f.progress}%</span>}
              {f.status === "processing" && <span style={{ color: COLORS.orange }}>Processing...</span>}
              {f.status === "complete" && <Icon name="status.ok" size={14} color={COLORS.green} />}
              {f.status === "error" && <Icon name="status.error" size={14} color={COLORS.pink} />}
              <span style={{ maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.filename}</span>
              <button onClick={() => removePendingFile(f.id)} style={{
                background: "none", border: "none", color: COLORS.textDim, cursor: "pointer", fontSize: 12, padding: 0,
              }}>{"\u00D7"}</button>
            </div>
          ))}
        </div>
      )}

      {imagePreview && (
        <div onClick={() => setImagePreview(null)} style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 2000,
          background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer",
        }}>
          <div style={{ maxWidth: "80vw", maxHeight: "80vh", position: "relative" }}>
            <img src={imagePreview.url} alt={imagePreview.filename} style={{ maxWidth: "100%", maxHeight: "80vh", borderRadius: 12 }} />
            <div style={{ textAlign: "center", color: "#fff", fontSize: 12, marginTop: 8 }}>{imagePreview.filename}</div>
          </div>
        </div>
      )}

      <div style={{
        padding: "12px 16px", borderTop: `1px solid ${COLORS.border}`,
        display: "flex", gap: 8, alignItems: "center",
      }}>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={!connected || noBuilder}
          title="Attach files or flowchart images"
          style={{
            width: 36, height: 36, borderRadius: 10, border: `1px solid ${COLORS.border}`,
            background: "transparent", color: COLORS.textMuted, cursor: connected && !noBuilder ? "pointer" : "default",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0,
          }}
        >{"\uD83D\uDCCE"}</button>
        <input type="file" ref={fileInputRef} onChange={handleFileUpload} multiple
          accept=".pdf,.docx,.doc,.txt,.csv,.html,.htm,.md,.json,.xml,.yaml,.yml,.py,.js,.ts,.jsx,.tsx,.png,.jpg,.jpeg,.gif,.webp,.bmp,.svg"
          style={{ display: "none" }} />
        <textarea
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={1}
          placeholder={
            noBuilder
              ? (directMode ? "Agent unreachable" : "Provision Super Agent first")
              : fileUploading ? "Uploading files..."
              : connected
                ? (directMode ? `Message ${agent?.name || "agent"}...` : "Describe or upload a flowchart...")
                : "Connecting..."}
          disabled={!connected || noBuilder || fileUploading}
          // Auto-grow up to ~6 lines via inline scrollHeight; cap at 140px
          // so the input doesn't take the whole panel for long pastes.
          // Word-wrap is on by default for textarea (overflow-wrap: anywhere
          // for long unbroken strings like URLs).
          ref={(el) => {
            if (!el) return;
            // Reset to single-row scrollHeight first so the height SHRINKS
            // when the user deletes lines.
            el.style.height = "auto";
            el.style.height = Math.min(el.scrollHeight, 140) + "px";
          }}
          style={{
            flex: 1, padding: "10px 14px", borderRadius: 12, fontSize: 13,
            background: COLORS.surface, border: `1px solid ${COLORS.border}`,
            color: COLORS.text, outline: "none", fontFamily: "inherit",
            resize: "none", overflowY: "auto",
            lineHeight: 1.4,
            maxHeight: 140,
            wordBreak: "break-word",
            overflowWrap: "anywhere",
          }}
        />
        <button
          onClick={handleSend}
          disabled={!canSend}
          style={{
            width: 40, height: 40, borderRadius: 12, border: "none",
            background: canSend ? COLORS.accent : COLORS.border,
            color: "#fff", fontSize: 16, cursor: canSend ? "pointer" : "default",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}
        >{"\u2191"}</button>
      </div>
    </div>
  );
}
